import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import { Row, Col, Button } from 'reactstrap';
import { getMaintenanceAutofill } from '../../services/MaintenanceServices';
import {
    ASSET_CATEGORIES,
    ASSET_SECTIONS,
    computeDerivedLine,
    emptyAssetLine,
    flattenGroupedLines,
    groupLinesByCategory,
    mergeAutofillSuggestions,
    parseGatePassRef,
    parseWorkScheduleRef,
    hasTransactionRef,
    stripLineRef,
    stripGatePassRef,
} from './maintenanceConstants';
import './MaintenanceAssetSections.css';

function TableNumberInput({ value, onChange, readOnly = false }) {
    return (
        <input
            type="number"
            step="0.001"
            className="form-control form-control-sm maintenance-table-input"
            value={value ?? 0}
            onChange={onChange}
            readOnly={readOnly}
        />
    );
}

function AssetTransactionTable({
    lines,
    readOnly,
    onUpdateLine,
    onRemoveRow,
    equipments,
    selectedEquipmentId,
    onSelectEquipment,
    equipType,
    qtyField,
    qtyLabel,
    emptyHint,
    vehicleLabel = 'Equipment',
}) {
    const equipOptions = useMemo(() => {
        const fromMaster = equipments.filter(
            (eq) => (eq.equip_type?.name || '').toUpperCase() === equipType.toUpperCase(),
        );
        const fromTrips = [];
        const seen = new Set(fromMaster.map((eq) => String(eq.id)));
        lines.forEach((line) => {
            if (!line.equipment_id || seen.has(String(line.equipment_id))) return;
            seen.add(String(line.equipment_id));
            fromTrips.push({
                id: line.equipment_id,
                equip_name: line.equipment_name || `${vehicleLabel} ${line.equipment_id}`,
            });
        });
        return [...fromMaster, ...fromTrips].sort((a, b) =>
            String(a.equip_name || '').localeCompare(String(b.equip_name || '')),
        );
    }, [equipments, lines, vehicleLabel]);

    const vehicleRows = useMemo(() => {
        if (!selectedEquipmentId) return [];
        return lines
            .map((line, index) => ({ line, index }))
            .filter(({ line }) => String(line.equipment_id || '') === String(selectedEquipmentId));
    }, [lines, selectedEquipmentId]);

    const totals = vehicleRows.reduce((acc, { line }) => ({
        qty: acc.qty + (Number(line[qtyField]) || 0),
        total_km: acc.total_km + (Number(line.total_km) || 0),
        running_hm: acc.running_hm + (Number(line.running_hm) || 0),
    }), { qty: 0, total_km: 0, running_hm: 0 });

    return (
        <div>
            <div className="maintenance-transit-vehicle-bar">
                <label className="asset-field-label mb-0">{vehicleLabel}</label>
                <select
                    className="form-control maintenance-transit-vehicle-select"
                    value={selectedEquipmentId || ''}
                    onChange={(event) => onSelectEquipment(event.target.value)}
                >
                    <option value="">Select {vehicleLabel}</option>
                    {equipOptions.map((eq) => (
                        <option key={eq.id} value={eq.id}>{eq.equip_name}</option>
                    ))}
                </select>
                {selectedEquipmentId && (
                    <span className="text-muted small">
                        {vehicleRows.length} transaction{vehicleRows.length === 1 ? '' : 's'}
                    </span>
                )}
            </div>

            {!selectedEquipmentId ? (
                <div className="maintenance-transit-empty">
                    {emptyHint}
                </div>
            ) : !vehicleRows.length ? (
                <div className="maintenance-transit-empty">
                    No job / DC transactions found for this equipment on the selected date.
                </div>
            ) : (
                <div className="table-responsive maintenance-transit-table-wrap">
                    <table className="table table-sm table-bordered maintenance-transit-table">
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>DC No</th>
                                <th>DC Time</th>
                                <th>Customer</th>
                                <th>Site</th>
                                <th>Grade</th>
                                <th>{qtyLabel}</th>
                                <th>Driver</th>
                                <th>Starting KM</th>
                                <th>Ending KM</th>
                                <th>Total KM</th>
                                <th>Starting HM</th>
                                <th>Ending HM</th>
                                <th>Total HM</th>
                                <th>Breakdown Hours</th>
                                <th>Idle Hours</th>
                                <th>Remarks</th>
                                {!readOnly && <th />}
                            </tr>
                        </thead>
                        <tbody>
                            {vehicleRows.map(({ line, index }, rowNo) => {
                                const isAuto = hasTransactionRef(line);
                                const handleField = (field) => (event) => {
                                    onUpdateLine(index, field, event.target.value);
                                };
                                return (
                                    <tr key={`tx-${line.gate_pass_id || line.work_schedule_id || line.id || index}`}>
                                        <td>{rowNo + 1}</td>
                                        <td>{line.dc_no || '-'}</td>
                                        <td>{line.dc_time || '-'}</td>
                                        <td>{line.customer_name || '-'}</td>
                                        <td>{line.site_name || '-'}</td>
                                        <td>{line.grade_name || '-'}</td>
                                        <td>
                                            <TableNumberInput
                                                value={line[qtyField]}
                                                onChange={handleField(qtyField)}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>{line.driver_name || '-'}</td>
                                        <td>
                                            <TableNumberInput
                                                value={line.opening_km}
                                                onChange={handleField('opening_km')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>
                                            <TableNumberInput
                                                value={line.closing_km}
                                                onChange={handleField('closing_km')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>{line.total_km ?? 0}</td>
                                        <td>
                                            <TableNumberInput
                                                value={line.opening_hm}
                                                onChange={handleField('opening_hm')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>
                                            <TableNumberInput
                                                value={line.closing_hm}
                                                onChange={handleField('closing_hm')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>{line.running_hm ?? 0}</td>
                                        <td>
                                            <TableNumberInput
                                                value={line.breakdown_hours}
                                                onChange={handleField('breakdown_hours')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>
                                            <TableNumberInput
                                                value={line.idle_hours}
                                                onChange={handleField('idle_hours')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        <td>
                                            <input
                                                className="form-control form-control-sm"
                                                value={stripLineRef(line.user_remarks)}
                                                onChange={handleField('user_remarks')}
                                                readOnly={readOnly}
                                            />
                                        </td>
                                        {!readOnly && (
                                            <td className="text-center">
                                                {!isAuto && (
                                                    <Button color="danger" size="sm" outline onClick={() => onRemoveRow(index)}>
                                                        X
                                                    </Button>
                                                )}
                                            </td>
                                        )}
                                    </tr>
                                );
                            })}
                        </tbody>
                        <tfoot>
                            <tr className="maintenance-transit-total-row">
                                <td colSpan={6} className="text-right"><strong>Total</strong></td>
                                <td><strong>{totals.qty.toFixed(3)}</strong></td>
                                <td />
                                <td colSpan={2} />
                                <td><strong>{totals.total_km.toFixed(3)}</strong></td>
                                <td colSpan={2} />
                                <td><strong>{totals.running_hm.toFixed(3)}</strong></td>
                                <td colSpan={readOnly ? 2 : 3} />
                            </tr>
                        </tfoot>
                    </table>
                </div>
            )}
        </div>
    );
}

function NumberInput({ label, value, onChange, readOnly = false, className = '' }) {
    return (
        <div className={`asset-field ${className}`.trim()}>
            <label className="asset-field-label">{label}</label>
            <input
                type="number"
                step="0.001"
                className="form-control asset-field-input"
                value={value ?? 0}
                onChange={onChange}
                readOnly={readOnly}
            />
        </div>
    );
}

function AssetRowFields({ category, line, onChange, equipments, readOnly }) {
    const handleField = (field) => (event) => {
        onChange(field, event.target.value);
    };

    const equipOptions = equipments.filter(
        (eq) => (eq.equip_type?.name || '').toUpperCase() === (
            ASSET_SECTIONS.find((s) => s.key === category)?.equipType || ''
        ).toUpperCase(),
    );

    return (
        <div className="border rounded p-2 mb-2 bg-light asset-row-card">
            <div className="asset-fields-grid">
                {category !== ASSET_CATEGORIES.PLANT && category !== ASSET_CATEGORIES.EB && (
                    [ASSET_CATEGORIES.GENERATOR, ASSET_CATEGORIES.LOADER].includes(category) ? (
                        <>
                            <div className="asset-field">
                                <label className="asset-field-label">Equipment</label>
                                <select
                                    className="form-control asset-field-input"
                                    value={line.equipment_id || ''}
                                    onChange={handleField('equipment_id')}
                                    disabled={readOnly}
                                >
                                    <option value="">Select Equipment</option>
                                    {equipOptions.map((eq) => (
                                        <option key={eq.id} value={eq.id}>{eq.equip_name}</option>
                                    ))}
                                </select>
                            </div>
                            <NumberInput label="Production Qty m³" value={line.production_qty} onChange={handleField('production_qty')} readOnly={readOnly} />
                        </>
                    ) : (
                        <div className="asset-field asset-field-span-2">
                            <label className="asset-field-label">Equipment</label>
                            <select
                                className="form-control asset-field-input"
                                value={line.equipment_id || ''}
                                onChange={handleField('equipment_id')}
                                disabled={readOnly}
                            >
                                <option value="">Select Equipment</option>
                                {equipOptions.map((eq) => (
                                    <option key={eq.id} value={eq.id}>{eq.equip_name}</option>
                                ))}
                            </select>
                        </div>
                    )
                )}

                {category === ASSET_CATEGORIES.PLANT && (
                    <>
                        <NumberInput label="Production m³" value={line.production_qty} onChange={handleField('production_qty')} readOnly={readOnly} />
                        <div className="asset-fields-row">
                            <NumberInput label="Start Hrs" value={line.opening_hm} onChange={handleField('opening_hm')} readOnly={readOnly} />
                            <NumberInput label="Close Hrs" value={line.closing_hm} onChange={handleField('closing_hm')} readOnly={readOnly} />
                            <NumberInput label="Running Hrs" value={line.running_hm} readOnly />
                        </div>
                    </>
                )}

                {category === ASSET_CATEGORIES.EB && (
                    <div className="asset-fields-row">
                        <NumberInput label="Opening kWh" value={line.opening_kwh} onChange={handleField('opening_kwh')} readOnly={readOnly} />
                        <NumberInput label="Closing kWh" value={line.closing_kwh} onChange={handleField('closing_kwh')} readOnly={readOnly} />
                        <NumberInput label="Units kWh" value={line.units_kwh} readOnly />
                    </div>
                )}

                {[ASSET_CATEGORIES.GENERATOR, ASSET_CATEGORIES.LOADER].includes(category) && (
                    <>
                        <div className="asset-fields-row">
                            <NumberInput label="Start HM" value={line.opening_hm} onChange={handleField('opening_hm')} readOnly={readOnly} />
                            <NumberInput label="Close HM" value={line.closing_hm} onChange={handleField('closing_hm')} readOnly={readOnly} />
                            <NumberInput label="Running Hrs" value={line.running_hm} readOnly={readOnly && line.source !== 'MANUAL'} onChange={handleField('running_hm')} />
                        </div>
                        {[ASSET_CATEGORIES.GENERATOR, ASSET_CATEGORIES.LOADER].includes(category) && (
                            <NumberInput label="Diesel L" value={line.diesel_litres} onChange={handleField('diesel_litres')} readOnly={readOnly} />
                        )}
                    </>
                )}

                {category === ASSET_CATEGORIES.TOWING && (
                    <div className="asset-fields-row">
                        <NumberInput label="Start KM" value={line.opening_km} onChange={handleField('opening_km')} readOnly={readOnly} />
                        <NumberInput label="Close KM" value={line.closing_km} onChange={handleField('closing_km')} readOnly={readOnly} />
                        <NumberInput label="Total KM" value={line.total_km} readOnly />
                    </div>
                )}

                {category !== ASSET_CATEGORIES.TRANSIT_MIXER && (
                    <>
                        <NumberInput label="Breakdown Hrs" value={line.breakdown_hours} onChange={handleField('breakdown_hours')} readOnly={readOnly} />
                        <NumberInput label="Idle Hrs" value={line.idle_hours} onChange={handleField('idle_hours')} readOnly={readOnly} />

                        <div className="asset-field asset-field-span-2">
                            <label className="asset-field-label">Remarks</label>
                            <input
                                className="form-control asset-field-input"
                                value={line.user_remarks || ''}
                                onChange={handleField('user_remarks')}
                                readOnly={readOnly}
                            />
                        </div>
                    </>
                )}
            </div>

            {line.source && line.source !== 'MANUAL' && (
                <div className="text-muted small mt-2 text-right">
                    Source: {line.source.replace(/_/g, ' ')}
                </div>
            )}
        </div>
    );
}

function lineHasData(line) {
    if (hasTransactionRef(line)) {
        return true;
    }
    const values = [
        line.production_qty, line.opening_km, line.closing_km, line.total_km,
        line.opening_hm, line.closing_hm, line.running_hm, line.opening_kwh,
        line.closing_kwh, line.units_kwh, line.diesel_litres, line.pump_qty,
        line.breakdown_hours, line.idle_hours, line.equipment_id,
    ];
    return values.some((value) => Number(value) > 0) || Boolean((line.user_remarks || '').trim());
}

const TRANSACTION_TABLE_CONFIG = {
    [ASSET_CATEGORIES.TRANSIT_MIXER]: {
        equipType: 'TRANSIT MIXER',
        qtyField: 'production_qty',
        qtyLabel: 'DC Qty',
        vehicleLabel: 'Vehicle',
        emptyHint: 'Select a vehicle to view its DC / gate transactions for this plant and date.',
        autofillSource: 'transit_mixer',
    },
    [ASSET_CATEGORIES.PUMP]: {
        equipType: 'PUMP',
        qtyField: 'pump_qty',
        qtyLabel: 'Pump Qty m³',
        vehicleLabel: 'Pump',
        emptyHint: 'Select a pump to view its job / DC transactions for this plant and date.',
        autofillSource: 'pump',
    },
};

function AssetSectionPanel({
    section,
    sectionIndex,
    totalSections,
    groupedLines,
    equipments,
    readOnly,
    onUpdateLine,
    onAddRow,
    onRemoveRow,
    onPreviousTab,
    onNextTab,
    canGoPrevious,
    canGoNext,
    onRefreshTransactions,
    transactionsLoading = false,
    selectedEquipmentId = '',
    onSelectEquipment,
}) {
    const lines = groupedLines[section.key]?.length
        ? groupedLines[section.key]
        : (section.transactionTable ? [] : [emptyAssetLine(section.key, 1)]);
    const txConfig = TRANSACTION_TABLE_CONFIG[section.key];

    return (
        <div className="maintenance-tab-panel">
            <Row className="align-items-center mb-3">
                <Col>
                    <div className="maintenance-tab-panel-title">{section.label}</div>
                    {section.transactionTable && (
                        <div className="text-muted small">
                            Select equipment to list all its DC / job transactions. Edit values for corrections, then save.
                        </div>
                    )}
                </Col>
                {!section.singleRow && !readOnly && (
                    <Col xs="auto" className="d-flex gap-2">
                        {section.transactionTable && (
                            <Button color="info" size="sm" outline onClick={onRefreshTransactions} disabled={transactionsLoading}>
                                {transactionsLoading ? 'Loading...' : 'Refresh Transactions'}
                            </Button>
                        )}
                        <Button
                            color="secondary"
                            size="sm"
                            onClick={() => onAddRow(section.key)}
                            disabled={section.transactionTable && !selectedEquipmentId}
                        >
                            Add Row
                        </Button>
                    </Col>
                )}
            </Row>
            {section.transactionTable && txConfig ? (
                <AssetTransactionTable
                    lines={lines}
                    readOnly={readOnly}
                    equipments={equipments}
                    selectedEquipmentId={selectedEquipmentId}
                    onSelectEquipment={onSelectEquipment}
                    equipType={txConfig.equipType}
                    qtyField={txConfig.qtyField}
                    qtyLabel={txConfig.qtyLabel}
                    emptyHint={txConfig.emptyHint}
                    vehicleLabel={txConfig.vehicleLabel}
                    onUpdateLine={(index, field, value) => onUpdateLine(section.key, index, field, value)}
                    onRemoveRow={(index) => onRemoveRow(section.key, index)}
                />
            ) : (
                lines.map((line, index) => (
                    <div key={`${section.key}-${index}`}>
                        <AssetRowFields
                            category={section.key}
                            line={line}
                            equipments={equipments}
                            readOnly={readOnly}
                            onChange={(field, value) => onUpdateLine(section.key, index, field, value)}
                        />
                        {!section.singleRow && lines.length > 1 && !readOnly && (
                            <div className="text-right mb-2">
                                <Button color="danger" size="sm" outline onClick={() => onRemoveRow(section.key, index)}>
                                    Remove
                                </Button>
                            </div>
                        )}
                    </div>
                ))
            )}

            <div className="maintenance-tab-nav">
                <Button
                    type="button"
                    color="secondary"
                    outline
                    className="maintenance-tab-nav-btn"
                    onClick={onPreviousTab}
                    disabled={!canGoPrevious}
                >
                    Previous
                </Button>
                <div className="maintenance-tab-nav-status">
                    Asset <strong>{sectionIndex + 1}</strong> of <strong>{totalSections}</strong>
                    {' · '}
                    {section.label}
                </div>
                <Button
                    type="button"
                    color="primary"
                    className="maintenance-tab-nav-btn"
                    onClick={onNextTab}
                    disabled={!canGoNext}
                >
                    Next
                </Button>
            </div>
        </div>
    );
}

export default function MaintenanceAssetSections({
    groupedLines,
    onGroupedChange,
    equipments,
    readOnly = false,
    plantId = '',
    logDate = '',
    cookies,
}) {
    const [activeTab, setActiveTab] = useState(ASSET_CATEGORIES.PLANT);
    const [transactionsLoading, setTransactionsLoading] = useState(false);
    const [selectedEquipmentByCategory, setSelectedEquipmentByCategory] = useState({});
    const lastTransactionLoadRef = useRef({});
    const groupedLinesRef = useRef(groupedLines);
    groupedLinesRef.current = groupedLines;

    useEffect(() => {
        lastTransactionLoadRef.current = {};
        setSelectedEquipmentByCategory({});
    }, [plantId, logDate]);

    useEffect(() => {
        [ASSET_CATEGORIES.TRANSIT_MIXER, ASSET_CATEGORIES.PUMP].forEach((category) => {
            if (selectedEquipmentByCategory[category]) return;
            const lines = groupedLines[category] || [];
            const firstWithEquip = lines.find((line) => line.equipment_id);
            if (firstWithEquip?.equipment_id) {
                setSelectedEquipmentByCategory((prev) => ({
                    ...prev,
                    [category]: String(firstWithEquip.equipment_id),
                }));
            }
        });
    }, [groupedLines, selectedEquipmentByCategory]);

    const tabCounts = useMemo(() => {
        const counts = {};
        ASSET_SECTIONS.forEach((section) => {
            const lines = groupedLines[section.key] || [];
            counts[section.key] = lines.filter(lineHasData).length;
        });
        return counts;
    }, [groupedLines]);

    const activeSection = ASSET_SECTIONS.find((section) => section.key === activeTab) || ASSET_SECTIONS[0];
    const activeTabIndex = ASSET_SECTIONS.findIndex((section) => section.key === activeTab);
    const canGoPrevious = activeTabIndex > 0;
    const canGoNext = activeTabIndex >= 0 && activeTabIndex < ASSET_SECTIONS.length - 1;

    const goToPreviousTab = () => {
        if (!canGoPrevious) return;
        setActiveTab(ASSET_SECTIONS[activeTabIndex - 1].key);
    };

    const goToNextTab = () => {
        if (!canGoNext) return;
        setActiveTab(ASSET_SECTIONS[activeTabIndex + 1].key);
    };

    const loadCategoryTransactions = useCallback((category, force = false) => {
        const txConfig = TRANSACTION_TABLE_CONFIG[category];
        if (!cookies || !plantId || !logDate || readOnly || !txConfig) {
            return Promise.resolve();
        }
        const loadKey = `${category}|${plantId}|${logDate}`;
        if (!force && lastTransactionLoadRef.current[category] === loadKey) {
            return Promise.resolve();
        }
        lastTransactionLoadRef.current[category] = loadKey;
        setTransactionsLoading(true);
        const displayDate = moment(logDate, 'YYYY-MM-DD').format('DD-MM-YYYY');
        return getMaintenanceAutofill(cookies, plantId, displayDate, txConfig.autofillSource)
            .then((response) => {
                const currentFlat = flattenGroupedLines(groupedLinesRef.current);
                const merged = mergeAutofillSuggestions(currentFlat, response.suggestions || []);
                onGroupedChange(groupLinesByCategory(merged));
            })
            .finally(() => setTransactionsLoading(false));
    }, [cookies, plantId, logDate, readOnly, onGroupedChange]);

    useEffect(() => {
        if (!TRANSACTION_TABLE_CONFIG[activeTab]) {
            return;
        }
        loadCategoryTransactions(activeTab, false);
    }, [activeTab, plantId, logDate, loadCategoryTransactions]);

    const updateLine = (category, index, field, value) => {
        const nextGrouped = { ...groupedLines };
        const lines = [...(nextGrouped[category] || [])];
        const isTextField = field === 'user_remarks' || field.includes('_name');
        const updated = computeDerivedLine({
            ...lines[index],
            [field]: field.includes('_id') || isTextField ? value : Number(value),
        });
        if (field === 'equipment_id') {
            const selected = equipments.find((eq) => String(eq.id) === String(value));
            updated.equipment_name = selected?.equip_name || '';
        }
        if (field === 'user_remarks') {
            if (parseGatePassRef(lines[index])) {
                updated.gate_pass_id = parseGatePassRef(lines[index]);
            }
            if (parseWorkScheduleRef(lines[index])) {
                updated.work_schedule_id = parseWorkScheduleRef(lines[index]);
            }
        }
        lines[index] = updated;
        nextGrouped[category] = lines;
        onGroupedChange(nextGrouped);
    };

    const addRow = (category) => {
        const nextGrouped = { ...groupedLines };
        const lines = [...(nextGrouped[category] || [])];
        const blank = emptyAssetLine(category, lines.length + 1);
        const selectedId = selectedEquipmentByCategory[category];
        if ([ASSET_CATEGORIES.TRANSIT_MIXER, ASSET_CATEGORIES.PUMP].includes(category) && selectedId) {
            const selected = equipments.find((eq) => String(eq.id) === String(selectedId));
            blank.equipment_id = selectedId;
            blank.equipment_name = selected?.equip_name || '';
        }
        lines.push(blank);
        nextGrouped[category] = lines;
        onGroupedChange(nextGrouped);
    };

    const removeRow = (category, index) => {
        const nextGrouped = { ...groupedLines };
        const lines = [...(nextGrouped[category] || [])];
        lines.splice(index, 1);
        nextGrouped[category] = lines;
        onGroupedChange(nextGrouped);
    };

    return (
        <div className="maintenance-asset-sections">
            <div className="maintenance-tab-bar" role="tablist" aria-label="Asset categories">
                {ASSET_SECTIONS.map((section) => {
                    const count = tabCounts[section.key] || 0;
                    return (
                        <button
                            key={section.key}
                            type="button"
                            role="tab"
                            aria-selected={activeTab === section.key}
                            className={`maintenance-tab${activeTab === section.key ? ' is-active' : ''}`}
                            onClick={() => setActiveTab(section.key)}
                        >
                            {section.label}
                            {count > 0 && (
                                <span className="maintenance-tab-badge">{count}</span>
                            )}
                        </button>
                    );
                })}
            </div>

            <AssetSectionPanel
                section={activeSection}
                sectionIndex={activeTabIndex >= 0 ? activeTabIndex : 0}
                totalSections={ASSET_SECTIONS.length}
                groupedLines={groupedLines}
                equipments={equipments}
                readOnly={readOnly}
                onUpdateLine={updateLine}
                onAddRow={addRow}
                onRemoveRow={removeRow}
                onPreviousTab={goToPreviousTab}
                onNextTab={goToNextTab}
                canGoPrevious={canGoPrevious}
                canGoNext={canGoNext}
                onRefreshTransactions={() => {
                    lastTransactionLoadRef.current[activeSection.key] = '';
                    loadCategoryTransactions(activeSection.key, true);
                }}
                transactionsLoading={transactionsLoading}
                selectedEquipmentId={selectedEquipmentByCategory[activeSection.key] || ''}
                onSelectEquipment={(value) => {
                    setSelectedEquipmentByCategory((prev) => ({
                        ...prev,
                        [activeSection.key]: value,
                    }));
                }}
            />
        </div>
    );
}
